import { surveyUploaderValueGetter } from '../../utils/columnValueGetters';
import Page from '../../components/Page';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Typography,
    FormControl,
    Button,
    Tooltip,
    Autocomplete,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { Folder } from 'react-feather';
import {
    DataGrid,
} from "@mui/x-data-grid";
import React from 'react';
import { useSelector } from "react-redux";
import Toast from "../../components/Toast";
import { isWithinUploadLimit, uploadSizeErrorMessage, MAX_UPLOAD_LABEL } from '../../utils/uploadLimits';
import { useUserMessages } from '../../hooks/useMessages';
import { useCustomerSurveys, useDeleteSurveyFiles, useDownloadSurveyFile, useUploadSurveyFile } from '../../hooks/useSurveys';

function normalizeProcessorOption(opt) {
    return {
        companyId: opt?.companyId,
        companyContact: opt?.companyContact ?? "N/A",
    };
}

function SurveyFileDropzone({ onUploadFile }) {
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone();

    const files = acceptedFiles.map(file => (
        <Tooltip key={file.path} title="Click to select different file">
            <ListItem sx={{ border: '1px solid black' }}>
                    <ListItemIcon>
                        <Folder/>
                    </ListItemIcon>
                    <ListItemText>
                        {file.path} - {file.size} bytes
                    </ListItemText>
            </ListItem>
        </Tooltip>
    ));

    return (
        <>
            <Typography>Upload Survey</Typography>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                flexDirection: 'column'
            }}>
                <Box
                    sx={{
                        width: '80%',
                        height: '70%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderWidth: '2px',
                        borderRadius: '2px',
                        borderStyle: 'dashed',
                        padding: '20px',
                        transition: 'border .24s ease-in-out'
                    }}
                    {...getRootProps({className: 'dropzone'})}
                >
                    <input {...getInputProps()}/>
                    { acceptedFiles.length > 0 ? <List>{files}</List> : (
                        <Typography>
                            {`Drag 'n' drop survey .txt file here, or click to select a survey .txt file (up to ${MAX_UPLOAD_LABEL}).`}
                        </Typography>
                    ) }
                </Box>
            </Box>
            <Button sx={{ width: '50%', margin: "10px" }}
                variant="contained"
                onClick={() => onUploadFile(acceptedFiles)}
            >
                Upload Survey File
            </Button>
        </>
    );
}

export function CustomerSurveyView({ companyIdOverride, embedded = false }){
    /// Customer Survey Tab
    const uploadSurveyFile = useUploadSurveyFile();
    const downloadSurveyFile = useDownloadSurveyFile();
    const userdata = useSelector((state)=> state.UserReducer.user);
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);
    const companyId = companyIdOverride ?? company?.id;
    const deleteSurveyFiles = useDeleteSurveyFiles();
    const { data: allSurveys = [], isFetching: isSurveyGridLoading } = useCustomerSurveys({
        companyId,
        enabled: Boolean(companyId),
        pauseRefetch: deleteSurveyFiles.isLoading,
    });
    const { data: allMessages = [] } = useUserMessages({
        userOrCompanyId: companyId,
        enabled: Boolean(companyId),
    });
    const isLoading = deleteSurveyFiles.isLoading || isSurveyGridLoading;

    const [selectedProcessor, setSelectedProcessor] = React.useState(null);
    const [dropzoneKey, setDropzoneKey] = React.useState(0);
    const [rowSelectionModel, setRowSelectionModel] = React.useState([]);

    const processorOptions = Array.isArray(allMessages) ? allMessages : [];

    const processorAutocompleteValue = React.useMemo(() => {
        if (processorOptions.length === 0) return null;
        if (processorOptions.length === 1) {
            return normalizeProcessorOption(processorOptions[0]);
        }
        const currentId = selectedProcessor?.companyId;
        const found = processorOptions.find((opt) => opt?.companyId === currentId);
        return found ? normalizeProcessorOption(found) : null;
    }, [processorOptions, selectedProcessor]);

    const autocompleteProps = {
        options: processorOptions,
        getOptionLabel: (option) => option?.companyContact ?? '',
    };


    const handleSurveyUpload = (acceptedFiles) => {
        if (acceptedFiles.length < 1) return Toast.fire({ title: 'File required', icon: 'error' });
        if (!isWithinUploadLimit(acceptedFiles[0])) return Toast.fire({ title: uploadSizeErrorMessage(), icon: 'error' });
        if (!acceptedFiles[0].name.includes('.txt')) return Toast.fire({ title: '.txt FILES ONLY', icon: 'error' });
        if (processorOptions.length === 0) {
            return Toast.fire({ title: "No processor contacts available", icon: "warning" });
        }
        if (processorOptions.length > 1 && processorAutocompleteValue == null) {
            return Toast.fire({ title: "Please Select Processor", icon: "warning" });
        }
        uploadSurveyFile.mutate(
            {
                file: acceptedFiles[0],
                ProcessorCompanyId: processorAutocompleteValue?.companyId,
                CustomerCompanyId: companyId,
                CustomerUploaderId: userdata.id,
            },
            {
                onSuccess: () => setDropzoneKey((k) => k + 1),
            },
        );
    };

    function multiDelete(items){
        swal({
            title: "DELETE FILE(S)",
            text: `Delete ${items.length} file(s) forever? This cannot be undone.`,
            icon: "warning",
            buttons: true,
            dangerMode: false,
        })
        .then((willUpdate) => {
            if (willUpdate) {
                deleteSurveyFiles.mutate(rowSelectionModel);
            } else {
                swal({
                    title: `File(s) not Deleted.`
                });
        }})
    };

    const handleDownload = (x) => {
        downloadSurveyFile.mutate({ fileId: x.fileId, name: x.name });
    };

    const rows = allSurveys;
    const columns = [
        { field: 'fileNameShort', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'processorName', headerName: 'Processor', width: 225, headerClassName: 'header'},
        { field: 'download', headerName: 'Download', width: 225, headerClassName: 'header', sortable: false, renderCell: (params) => {
            return (<Button variant="contained" color="success" onClick={() => handleDownload({fileId:params.row.id, name:params.row.fileNameShort})}>
            Download</Button>
            )}
        },
        { field: 'uploader', headerName: 'Uploader', width: 225, headerClassName: 'header', valueGetter: surveyUploaderValueGetter, renderCell: (params) => {
        return <span>{params.row.customerFirstName} {params.row.customerLastName}</span>
        }},
        { field: 'updated', headerName: 'Updated', width: 175, headerClassName: 'header', renderCell: (params) => {
            let date = new Date(params.row.updated);
            return date.toLocaleString()
        }},
        { field: 'Delete', headerName: 'Delete', width: 125, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                return(
                    <Button className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                        onClick={() => {
                            swal({
                                title: "DELETE FILE",
                                text: `Delete this file forever? This cannot be undone.`,
                                icon: "warning",
                                buttons: true,
                                dangerMode: false,
                            })
                            .then((willUpdate) => {
                                if (willUpdate) {
                                    deleteSurveyFiles.mutate([cellValues.row.id]);
                                } else {
                                    swal({
                                        title: `File not Deleted.`
                                    });
                            }})
                    }}>
                        Delete
                    </Button>
                )
            }
        },
    ];

    const body = (
        <Box sx={{ textAlign: "center" }}>
            {(
                <Box sx={{width:"100%", height:"100%", marginTop: '0%'}}>
                    { processorOptions.length > 1 &&
                        <Box component="form" sx={{display:'flex', flexDirection:'column', alignItems:'center'}}>
                            <FormControl style={{width:"50%"}}>
                                <Autocomplete
                                    disablePortal
                                    {...autocompleteProps}
                                    value={processorAutocompleteValue}
                                    isOptionEqualToValue={(option, value) =>
                                        option?.companyId === value?.companyId}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Processor Select"
                                            placeholder="Select a processor"
                                        />
                                    )}
                                    onChange={(event, newValue) => setSelectedProcessor(newValue)}
                                />
                            </FormControl>
                        </Box>
                    }
                    { processorOptions.length == 1 &&
                        <Typography>
                            Send Survey Files to{" "}
                            <Typography component="span" fontWeight="bold">
                                {processorOptions[0].companyContact}
                            </Typography>
                        </Typography>
                    }
                    { processorOptions.length == 0 &&
                        <Typography>
                            Connect with a processor via Contacts to send survey files.
                        </Typography>
                    }
                    <SurveyFileDropzone
                        key={dropzoneKey}
                        onUploadFile={handleSurveyUpload}
                    />
                </Box>
            )}
            <Typography sx={{textAlign:'left'}}>
                    Select the plans you want to delete.
                    {rowSelectionModel.length > 0 ?
                        <Button
                            className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                            onClick={() => multiDelete(rowSelectionModel)}
                        >
                            Delete
                        </Button>
                        :
                        <Button
                            disabled
                            className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                        >
                            Delete
                        </Button>
                    }
                </Typography>
            <Box sx={{ height: '70vh', width: '100%', mt: 1 }}>
                <DataGrid
                    style={{textAlign:'left'}}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel, params) => {
                        setRowSelectionModel(newRowSelectionModel);
                    }}
                    rowSelectionModel={rowSelectionModel}
                    disableRowSelectionOnClick
                    columns={columns}
                    rows={rows}
                    loading={isLoading}
                    pageSizeOptions={[10, 25, 50, 100]}
                />
            </Box>
        </Box>
    );

    if (embedded) return body;
    return (
        <Page title="Surveys" style={{textAlign:'center'}}>
            {body}
        </Page>
    );
};
